import { useState, useEffect, useMemo, useCallback } from 'react'
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart,
} from 'recharts'
import { ChevronRight } from 'lucide-react'
import { govdataAPI } from '../../services/govdata'
import SectionCard from './SectionCard'
import type { AcqPerformanceRollup, HierarchyLevel, VendorConcentration } from '../../types/govdata'
import { parseNum, formatDollars, formatNumber } from '../../types/govdata'

const NEXT_LEVEL: Record<HierarchyLevel, HierarchyLevel | null> = {
  agency: 'macom',
  macom: 'subcom1',
  subcom1: 'office',
  office: null,
}

const LEVEL_LABELS: Record<HierarchyLevel, string> = {
  agency: 'Departments',
  macom: 'Major Commands (MAJCOM)',
  subcom1: 'Sub-Commands',
  office: 'Offices',
}

interface BreadcrumbEntry {
  level: HierarchyLevel
  code: string | undefined
  name: string
}

interface Props {
  fiscalYear?: number
  embedded?: boolean
}

export default function VendorConcentrationSection({ fiscalYear, embedded = false }: Props) {
  // Hierarchy drill state
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbEntry[]>([
    { level: 'agency', code: undefined, name: 'All' },
  ])
  const [hierarchyData, setHierarchyData] = useState<AcqPerformanceRollup[]>([])
  const [hierarchyLoading, setHierarchyLoading] = useState(true)

  // Vendor detail state (shown when drilled to office level)
  const [vendorData, setVendorData] = useState<VendorConcentration[]>([])
  const [vendorLoading, setVendorLoading] = useState(false)
  const [vendorError, setVendorError] = useState<string | null>(null)
  const [vendorTotal, setVendorTotal] = useState(0)

  const currentLevel = breadcrumbs[breadcrumbs.length - 1]
  const nextLevel = NEXT_LEVEL[currentLevel.level]
  const queryLevel = nextLevel || currentLevel.level
  const isAtOfficeLevel = queryLevel === 'office' && !nextLevel

  // Fetch hierarchy data (for drill-down navigation)
  useEffect(() => {
    if (isAtOfficeLevel) return
    setHierarchyLoading(true)
    const params: Record<string, any> = {
      hierarchy_level: queryLevel,
      sort_by: '-total_obligated',
    }
    if (currentLevel.code) params.parent_code = currentLevel.code
    if (fiscalYear) params.fiscal_year = fiscalYear
    govdataAPI.acqPerformance(params)
      .then(resp => {
        setHierarchyData(resp.results)
        setHierarchyLoading(false)
      })
      .catch(() => setHierarchyLoading(false))
  }, [queryLevel, currentLevel.code, fiscalYear, isAtOfficeLevel])

  // Fetch vendor data when at office level
  useEffect(() => {
    if (!isAtOfficeLevel || !currentLevel.code) return
    setVendorLoading(true)
    setVendorError(null)
    govdataAPI
      .vendorConcentration(
        { fiscal_year: fiscalYear, office_code: currentLevel.code },
        { page: 1, page_size: 20 },
      )
      .then((res) => {
        setVendorData(res.results)
        setVendorTotal(res.count)
        setVendorLoading(false)
      })
      .catch((err) => {
        setVendorError(err.message)
        setVendorLoading(false)
      })
  }, [isAtOfficeLevel, currentLevel.code, fiscalYear])

  const handleDrill = useCallback((code: string, name: string) => {
    if (!nextLevel) return
    setBreadcrumbs(prev => [
      ...prev,
      { level: nextLevel, code, name },
    ])
  }, [nextLevel])

  const handleBreadcrumbClick = (idx: number) => {
    setBreadcrumbs(prev => prev.slice(0, idx + 1))
  }

  // Hierarchy bar chart data
  const chartItems = useMemo(() => {
    return hierarchyData
      .filter(r => r.total_obligated != null && r.total_obligated > 0)
      .sort((a, b) => (b.total_obligated ?? 0) - (a.total_obligated ?? 0))
      .slice(0, 20)
      .map(r => ({
        code: r.entity_code,
        label: r.entity_name || r.entity_code,
        total_obligated: r.total_obligated ?? 0,
        total_awards: r.total_awards,
        child_count: r.child_count,
      }))
  }, [hierarchyData])

  // Pareto chart data (office-level vendors)
  const paretoData = useMemo(() => {
    return vendorData
      .sort((a, b) => a.dollar_rank - b.dollar_rank)
      .slice(0, 20)
      .map((r) => ({
        name: (r.recipient_name || 'Unknown').slice(0, 20),
        dollars: parseNum(r.total_obligated),
        share: parseNum(r.cumulative_share) * 100,
      }))
  }, [vendorData])

  const loading = isAtOfficeLevel ? vendorLoading : hierarchyLoading
  const error = isAtOfficeLevel ? vendorError : null
  const drillLabel = nextLevel ? LEVEL_LABELS[nextLevel] : LEVEL_LABELS[queryLevel]

  return (
    <SectionCard
      title="Vendor Concentration"
      subtitle={isAtOfficeLevel
        ? `Top vendors by obligated dollars${vendorTotal > 0 ? ` (${vendorTotal.toLocaleString()} total)` : ''}`
        : `Comparing ${drillLabel.toLowerCase()} — total obligated dollars`
      }
      loading={loading}
      error={error}
      embedded={embedded}
    >
      {/* Breadcrumb */}
      {breadcrumbs.length > 1 && (
        <div className="flex items-center gap-1.5 text-sm mb-4">
          {breadcrumbs.map((bc, idx) => (
            <span key={idx} className="flex items-center gap-1.5">
              {idx > 0 && <ChevronRight className="w-3.5 h-3.5 text-gray-400 shrink-0" />}
              {idx < breadcrumbs.length - 1 ? (
                <button
                  onClick={() => handleBreadcrumbClick(idx)}
                  className="font-medium text-blue-600 hover:text-blue-800 hover:underline whitespace-nowrap"
                >
                  {bc.name}
                </button>
              ) : (
                <span className="font-medium text-gray-900 whitespace-nowrap">{bc.name}</span>
              )}
            </span>
          ))}
        </div>
      )}

      {isAtOfficeLevel ? (
        // Office-level: show vendor Pareto chart + table
        paretoData.length === 0 ? (
          <div className={`${embedded ? 'h-full' : 'h-64'} flex items-center justify-center text-sm text-gray-400`}>No data</div>
        ) : (
          <div className={embedded ? 'flex flex-col h-full min-h-0' : ''}>
            <div className={`mb-4 shrink-0 ${embedded ? 'h-56' : 'h-56'}`}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={paretoData} margin={{ top: 5, right: 30, bottom: 5, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f1f1" />
                  <XAxis dataKey="name" tick={{ fontSize: 8, fill: '#6b7280' }} tickLine={false} axisLine={false} angle={-45} textAnchor="end" height={60} />
                  <YAxis yAxisId="left" tick={{ fontSize: 10, fill: '#6b7280' }} tickLine={false} axisLine={false} tickFormatter={(v) => formatDollars(v)} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: '#6b7280' }} tickLine={false} axisLine={false} tickFormatter={(v) => `${v}%`} domain={[0, 100]} />
                  <Tooltip formatter={(v: any, name: any) => name === 'Cumulative %' ? `${Number(v).toFixed(1)}%` : formatDollars(v)} />
                  <Bar yAxisId="left" dataKey="dollars" fill="#3b82f6" name="Obligated" barSize={14} />
                  <Line yAxisId="right" dataKey="share" stroke="#ef4444" strokeWidth={2} dot={false} name="Cumulative %" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div className={embedded ? 'flex-1 min-h-0 overflow-auto' : 'overflow-x-auto'}>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-1.5 pr-2 font-medium text-gray-600">#</th>
                    <th className="text-left py-1.5 pr-2 font-medium text-gray-600">Vendor</th>
                    <th className="text-right py-1.5 px-2 font-medium text-gray-600">Awards</th>
                    <th className="text-right py-1.5 px-2 font-medium text-gray-600">Obligated</th>
                    <th className="text-right py-1.5 px-2 font-medium text-gray-600">Share</th>
                    <th className="text-right py-1.5 px-2 font-medium text-gray-600">Cumul.</th>
                  </tr>
                </thead>
                <tbody>
                  {vendorData.slice(0, 20).map((r, i) => (
                    <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-1.5 pr-2 text-gray-500">{r.dollar_rank}</td>
                      <td className="py-1.5 pr-2 text-gray-800 truncate max-w-[200px]">{r.recipient_name || '\u2014'}</td>
                      <td className="py-1.5 px-2 text-right text-gray-700">{(r.award_count || 0).toLocaleString()}</td>
                      <td className="py-1.5 px-2 text-right text-gray-700">{formatDollars(r.total_obligated)}</td>
                      <td className="py-1.5 px-2 text-right text-gray-700">{(parseNum(r.dollar_share) * 100).toFixed(1)}%</td>
                      <td className="py-1.5 px-2 text-right text-gray-700">{(parseNum(r.cumulative_share) * 100).toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      ) : (
        // Hierarchy level: show bar chart of total obligated by entity.
        // Embedded mode (inside a dashboard widget tile) fills the
        // available space; standalone page uses a per-row height that
        // grows with the entity count.
        chartItems.length === 0 ? (
          <div className={`${embedded ? 'h-full' : 'h-80'} flex items-center justify-center text-sm text-gray-400`}>
            {loading ? '' : 'No data at this level'}
          </div>
        ) : (
          <div
            className={embedded ? 'h-full w-full min-h-0' : ''}
            style={embedded ? undefined : { height: Math.max(400, chartItems.length * 36) }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartItems}
                layout="vertical"
                margin={{ top: 5, right: 20, bottom: 5, left: 5 }}
                barSize={embedded ? Math.max(8, Math.min(18, 220 / Math.max(chartItems.length, 1))) : 18}
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f1f1" />
                <XAxis
                  type="number"
                  tick={{ fontSize: 10, fill: '#6b7280' }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => formatDollars(v)}
                />
                <YAxis
                  type="category"
                  dataKey="label"
                  tick={{ fontSize: 9, fill: '#6b7280' }}
                  tickLine={false}
                  axisLine={false}
                  width={170}
                  tickFormatter={(v: string) => v ? (v.length > 26 ? v.slice(0, 24) + '\u2026' : v) : ''}
                />
                <Tooltip
                  formatter={(v: any) => formatDollars(v)}
                  labelFormatter={(label) => {
                    const item = chartItems.find(d => d.label === label)
                    return `${label} (${formatNumber(item?.total_awards ?? 0)} awards)`
                  }}
                />
                <Bar
                  dataKey="total_obligated"
                  fill="#3b82f6"
                  name="Total Obligated"
                  cursor={nextLevel ? 'pointer' : 'default'}
                  onClick={(_: any, idx: number) => {
                    const item = chartItems[idx]
                    if (item && nextLevel) handleDrill(item.code, item.label)
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )
      )}
    </SectionCard>
  )
}
